import { useState } from "react";
import { useQuery } from "@apollo/client";
import { LinkProps } from "@generouted/react-router/client";
import Tag from "@/components/Tag";
import type { Action } from "@/components/MenuButton";
import Table, {
  TableHeading,
  TableControls,
  toTableControlActions,
  buildActionColumn,
  buildTwoLineColumn,
  buildExpandableCountColumn,
  type TableAction
} from "@/components/Table";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import MetricGrid from "@/components/MetricGrid";
import EmptyState from "@/components/EmptyState";
import { MaterialNames } from "@/components/material_symbol_names";
import type { Path, Params } from "@/router";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useFlags } from "@/hooks/useFeatureFlags";
import { GET_PGM_PROGRAM_TABLE_DATA_QUERY, PGMProgramTableData } from "./queries";
import { buildPATStatusForMetric } from "@/pages/education/universities/[universityId]/students/_utils/patStatus";
import { buildPgmUniversityMetric } from "@/features/person/utils/pgm-university-metric";
import { EducationPgmProgram, type PgmProgramRequirement } from "@/types";
import { canEditEducationRequirementType } from "@/features/person/utils/education-requirement-reason-codes";
import { formatProgramData, type ProgramDetailRow, type ProgramProgressRow } from "./utils";
import ArchiveEducationProgramLevelDialog, {
  type EducationProgramLevelToArchive
} from "./ArchiveEducationProgramLevelDialog";
import DeleteProgramRequirementDialog, { type ProgramRequirementToDelete } from "./DeleteProgramRequirementDialog";
import DeleteEducationProgramLevelDialog, {
  type EducationProgramLevelToDelete
} from "./DeleteEducationProgramLevelDialog";
import EditEducationRequirementModal from "./EditEducationRequirementModal";
import AddEducationProgramLevelModal from "./AddEducationProgramLevelModal";
import EditEducationProgramLevelModal, { type EducationProgramLevelToEdit } from "./EditEducationProgramLevelModal";
import {
  INCOMPLETE_PGM_LEVEL_MESSAGE,
  personHasIncompletePgmLevel
} from "@/features/person/utils/education-program-level-options";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import { useParams } from "@/hooks/useSearchParams";

export interface PGMLatestProgram {
  level: string | number;
  curriculumVersion: string;
  startDate: string;
  endDate?: string | null;
  completed: boolean;
  programType: string;
  pgmProgramCompleted?: boolean;
  pgmProgramExpirationDate?: string | null;
}

export interface PGMMetric {
  label: string;
  value: string | null;
  iconName: MaterialNames;
  variant?: "success" | "warn" | "error";
  loading?: boolean;
  link?: LinkProps<Path, Params>;
  tooltip?: {
    title: string;
    supportingText: string;
    actions?: {
      primary: React.ReactNode;
    };
  };
}

export interface PGMProgramTableProps {
  personId: string;
  tableHeading?: string;
  tableBody?: string;
  showMetrics?: boolean;
  testId?: string;
  actions?: Array<{
    label: string;
    icon?: MaterialNames;
    variant?: "filled" | "outlined" | "text";
    onClick: () => void;
    testId?: string;
  }>;
  customMetrics?: PGMMetric[];
  context?: "education" | "membership" | "student";
  canManageEducationRequirements?: boolean;
  canDeleteEducationProgramLevels?: boolean;
}

const getProgramText = (
  programHistory: EducationPgmProgram[],
  latestProgram?: PGMLatestProgram,
  context?: "education" | "membership" | "student"
): { heading?: string; body?: string } => {
  const programType = latestProgram?.programType || programHistory[0]?.programType || "PGM";

  if (context === "student") {
    return {
      heading: "PGM Program Progress",
      body: "Program Type: Student"
    };
  } else if (context === "education") {
    return {};
  } else {
    return {
      heading: `${programType} Program`,
      body: `View ${programType.toLowerCase()} program history for the PGA Professional Golf Management (PGM)`
    };
  }
};

const buildLastAttendedPgmUniversityMetric = (person: PGMProgramTableData["person"]): PGMMetric | null => {
  const metric = buildPgmUniversityMetric(person);
  if (!metric) return null;
  return metric as PGMMetric;
};

// Function to build default metrics from person data
const buildDefaultMetrics = (person: PGMProgramTableData["person"]): PGMMetric[] => {
  const liveProgramHistory = (person.educationPgmProgramHistory || []).filter((program) => !program.archived);
  const patStatus = buildPATStatusForMetric(person.prospectProgram?.playingAbilityTestStatus);
  const uniMetric = buildLastAttendedPgmUniversityMetric(person);

  return [
    ...(uniMetric ? [uniMetric] : []),
    {
      label: "Current Level",
      value: person.educationLatestPgmProgram ? `Level ${person.educationLatestPgmProgram.level}` : "--",
      iconName: "school" as MaterialNames
    },
    {
      label: "PGM Requirements",
      value: `${liveProgramHistory.filter((p) => p.completed).length}/${liveProgramHistory.length}`,
      iconName: "school" as MaterialNames,
      variant: liveProgramHistory.filter((p) => p.completed).length < liveProgramHistory.length ? "warn" : "success"
    },
    {
      label: "PAT Status",
      value: patStatus.value,
      iconName: "sports_golf" as MaterialNames,
      variant: patStatus.variant === "info" ? "warn" : patStatus.variant
    }
  ];
};

export default function PGMProgramTable(props: PGMProgramTableProps) {
  const {
    showMetrics = true,
    testId = "pgm-program-table",
    actions = [],
    customMetrics,
    context,
    personId,
    canManageEducationRequirements = false,
    canDeleteEducationProgramLevels = false
  } = props;

  const { params, addSearchParam, deleteSearchParams } = useParams();
  const showArchived = context === "education" && params.showArchived === "true";

  const setShowArchived = (value: boolean) => {
    if (value) {
      addSearchParam("showArchived", "true");
    } else {
      deleteSearchParams("showArchived");
    }
  };

  const { data, previousData, loading, error } = useQuery<PGMProgramTableData>(GET_PGM_PROGRAM_TABLE_DATA_QUERY, {
    variables: {
      pgaId: personId,
      includeArchived: showArchived
    },
    fetchPolicy: "cache-first",
    skip: !personId
  });

  if (error) {
    return <EmptyState title="Error Loading Program Data" caption={error.message} iconName="error" />;
  }

  const personData = data ?? previousData;

  if (loading && !personData?.person) {
    const skeletonMetrics = showMetrics
      ? customMetrics && customMetrics.length > 0
        ? customMetrics
        : [
            { label: "Current Level", value: "", iconName: "school" as MaterialNames, loading: true },
            { label: "PGM Requirements", value: "", iconName: "school" as MaterialNames, loading: true },
            { label: "PAT Status", value: "", iconName: "sports_golf" as MaterialNames, loading: true }
          ]
      : [];

    return (
      <PGMProgramTableContent
        latestProgram={undefined}
        loading={true}
        metrics={skeletonMetrics}
        tableHeading={props.tableHeading}
        tableBody={props.tableBody}
        showMetrics={showMetrics}
        testId={testId}
        actions={actions}
        tableData={[]}
        canManageEducationRequirements={canManageEducationRequirements}
        canDeleteEducationProgramLevels={canDeleteEducationProgramLevels}
        personId={personId}
        context={context}
        hasIncompleteLevel={false}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        hasArchivedEducationPgmProgramHistory={false}
      />
    );
  }

  if (!personData?.person) {
    return (
      <EmptyState
        title="No Program Data Found"
        caption="Unable to load program information for this person"
        iconName="school"
      />
    );
  }

  const person = personData.person;

  const programHistory = person.educationPgmProgramHistory || [];
  const liveProgramHistory = programHistory.filter((program) => !program.archived);

  const latestProgram: PGMLatestProgram | undefined = person.educationLatestPgmProgram
    ? {
        level: person.educationLatestPgmProgram.level,
        curriculumVersion: person.educationLatestPgmProgram.curriculumVersion,
        startDate: person.educationLatestPgmProgram.startDate,
        endDate: person.educationLatestPgmProgram.endDate,
        completed: person.educationLatestPgmProgram.completed,
        programType: person.educationLatestPgmProgram.programType,
        pgmProgramCompleted: person.educationLatestPgmProgram.pgmProgramCompleted,
        pgmProgramExpirationDate: person.educationLatestPgmProgram.pgmProgramExpirationDate
      }
    : undefined;

  const metrics = customMetrics || buildDefaultMetrics(person);
  const tableData = formatProgramData(programHistory);
  const hasIncompleteLevel = personHasIncompletePgmLevel(liveProgramHistory);
  const hasArchivedEducationPgmProgramHistory = person.hasArchivedEducationPgmProgramHistory;

  const programText = getProgramText(programHistory, latestProgram, context);

  return (
    <PGMProgramTableContent
      latestProgram={latestProgram}
      loading={loading && !data?.person}
      metrics={metrics}
      tableHeading={props.tableHeading ?? programText.heading}
      tableBody={props.tableBody ?? programText.body}
      showMetrics={showMetrics}
      testId={testId}
      actions={actions}
      tableData={tableData}
      canManageEducationRequirements={canManageEducationRequirements}
      canDeleteEducationProgramLevels={canDeleteEducationProgramLevels}
      personId={personId}
      context={context}
      hasIncompleteLevel={hasIncompleteLevel}
      showArchived={showArchived}
      setShowArchived={setShowArchived}
      hasArchivedEducationPgmProgramHistory={hasArchivedEducationPgmProgramHistory}
    />
  );
}

// Extracted content component to avoid duplication
interface PGMProgramTableContentProps {
  latestProgram?: PGMLatestProgram;
  loading: boolean;
  metrics: PGMMetric[];
  tableHeading?: string;
  tableBody?: string;
  showMetrics: boolean;
  testId: string;
  actions: Array<{
    label: string;
    icon?: MaterialNames;
    variant?: "filled" | "outlined" | "text";
    onClick: () => void;
    testId?: string;
  }>;
  tableData: ProgramProgressRow[];
  canManageEducationRequirements: boolean;
  canDeleteEducationProgramLevels: boolean;
  personId: string;
  context?: "education" | "membership" | "student";
  hasIncompleteLevel?: boolean;
  showArchived?: boolean;
  setShowArchived?: (value: boolean) => void;
  hasArchivedEducationPgmProgramHistory?: boolean;
}

function PGMProgramTableContent({
  latestProgram,
  loading,
  metrics,
  tableHeading,
  tableBody,
  showMetrics,
  testId,
  actions,
  tableData,
  canManageEducationRequirements,
  canDeleteEducationProgramLevels,
  personId,
  context,
  hasIncompleteLevel = false,
  showArchived = false,
  setShowArchived,
  hasArchivedEducationPgmProgramHistory = false
}: PGMProgramTableContentProps) {
  const [requirementToDelete, setRequirementToDelete] = useState<ProgramRequirementToDelete | null>(null);
  const [levelToDelete, setLevelToDelete] = useState<EducationProgramLevelToDelete | null>(null);
  const [selectedRequirement, setSelectedRequirement] = useState<{
    requirement: PgmProgramRequirement;
    levelStartDate: string;
  } | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<EducationProgramLevelToEdit | null>(null);
  const [levelToArchive, setLevelToArchive] = useState<EducationProgramLevelToArchive | null>(null);
  const [showAddLevelModal, setShowAddLevelModal] = useState(false);
  const { Menu, setMenu } = useTableRowMenu();
  const flags = useFlags();
  const showEditEducationRequirementActions = canManageEducationRequirements;
  const showDeleteEducationProgramLevelActions =
    canDeleteEducationProgramLevels && !!flags["backoffice-education-delete-program-level"];

  const openAddLevel = () => {
    if (hasIncompleteLevel) {
      showErrorSnackbar(INCOMPLETE_PGM_LEVEL_MESSAGE);
      return;
    }

    setShowAddLevelModal(true);
  };

  const headingActions: TableAction[] =
    showEditEducationRequirementActions && context === "education" && !loading
      ? [
          ...actions,
          {
            label: "Add Level",
            icon: "add",
            variant: "outlined",
            testId: "add-education-program-level-action",
            onClick: openAddLevel,
            ariaLabel: hasIncompleteLevel ? `Add Level. ${INCOMPLETE_PGM_LEVEL_MESSAGE}` : "Add Level"
          }
        ]
      : actions;
  const tableControlActions = toTableControlActions(headingActions);

  const showArchivedChip =
    context === "education" && (tableData.length > 0 || hasArchivedEducationPgmProgramHistory || showArchived);
  const headingCopy = tableHeading;
  const headingBody =
    tableBody || (latestProgram && tableHeading ? `Program Type: ${latestProgram.programType}` : undefined);
  const hasHeadingCopy = Boolean(headingCopy || headingBody);
  const hasMetrics = showMetrics && metrics.length > 0;
  const showControls = tableControlActions.length > 0 || showArchivedChip;
  // Build main table columns
  const columns = [
    buildTwoLineColumn<ProgramProgressRow>({
      accessorKey: "level",
      header: "Level",
      getPrimaryText: (row) => row.level,
      getSecondaryText: (row) => row.code,
      size: 300,
      isFirstColumn: true
    }),
    {
      accessorKey: "version",
      header: "Version",
      enableSorting: false
    },
    {
      accessorKey: "stage",
      header: "Stage",
      enableSorting: false
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      enableSorting: false
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      enableSorting: false
    },
    {
      accessorKey: "expiresDate",
      header: "Expiration",
      enableSorting: false,
      cell: ({ row }: { row: { original: ProgramProgressRow } }) => (
        <span className={row.original.isExpired ? "text-sys-brand-error" : ""}>{row.original.expiresDate || "--"}</span>
      )
    },
    buildExpandableCountColumn<ProgramProgressRow>({
      accessorKey: "requirements",
      header: "Requirements",
      getCount: (row) => row.completedDetailsCount,
      getTotal: (row) => row.detailsCount
    }),
    {
      accessorKey: "status",
      header: "Program Status",
      cell: ({ row }: { row: { original: ProgramProgressRow } }) => (
        <Tag
          tagType={
            row.original.status === "Complete" ? "success" : row.original.status === "Archived" ? "surface" : "warn"
          }
          size="small"
        >
          {row.original.status}
        </Tag>
      ),
      meta: {
        headerClassName: "hide-header-in-column"
      },
      enableSorting: false
    },
    ...((showEditEducationRequirementActions || showDeleteEducationProgramLevelActions) && context === "education"
      ? [
          buildActionColumn<ProgramProgressRow>({
            getActions: (row): Action[] => {
              if (row.archived) return [];

              return [
                ...(showEditEducationRequirementActions
                  ? ([
                      {
                        text: "Edit Level",
                        section: "Manage",
                        testId: "edit-education-program-level-action",
                        onClick: () =>
                          setSelectedLevel({
                            completeCode: row.completeCode,
                            endDateIso: row.endDateIso,
                            levelCode: row.code,
                            levelLabel: row.level,
                            remarks: row.remarks,
                            startDateIso: row.startDateIso
                          })
                      },
                      {
                        text: "Archive Level",
                        iconName: "archive",
                        destructive: true,
                        testId: "archive-education-program-level-action",
                        onClick: () =>
                          setLevelToArchive({
                            levelCode: row.code,
                            levelLabel: row.level
                          })
                      }
                    ] satisfies Action[])
                  : []),
                ...(showDeleteEducationProgramLevelActions
                  ? ([
                      {
                        text: "Delete Level",
                        iconName: "delete",
                        destructive: true,
                        testId: "delete-education-program-level-action",
                        onClick: () =>
                          setLevelToDelete({
                            levelLabel: row.level,
                            levelCode: row.code,
                            startDateIso: row.startDateIso
                          })
                      }
                    ] satisfies Action[])
                  : [])
              ];
            },
            actionId,
            setMenu,
            getRowId: (row) => row.id
          })
        ]
      : [])
  ];

  const detailsColumns = [
    buildTwoLineColumn<ProgramDetailRow>({
      accessorKey: "description",
      header: "Description",
      getPrimaryText: (row) => row.description,
      getSecondaryText: (row) => row.secondaryText,
      size: 300,
      isFirstColumn: true
    }),
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => <span>{detailRow.original.type}</span>,
      enableSorting: false,
      size: 120
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <span>{detailRow.original.date || "--"}</span>
      ),
      enableSorting: false,
      size: 150
    },
    {
      accessorKey: "scoreText",
      header: "Score",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) =>
        detailRow.original.scoreText ? (
          <span>{detailRow.original.scoreText}</span>
        ) : (
          <span className="opacity-20">--</span>
        ),
      enableSorting: false,
      size: 100
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row: detailRow }: { row: { original: ProgramDetailRow } }) => (
        <Tag tagType={detailRow.original.statusType} size="small">
          {detailRow.original.statusLabel}
        </Tag>
      ),
      meta: {
        headerClassName: "hide-header-in-column"
      },
      enableSorting: false,
      size: 120
    },
    ...(canManageEducationRequirements
      ? [
          buildActionColumn<ProgramDetailRow>({
            getActions: (row) => {
              if (row.type !== "Requirement" || !row.requireId) return [];
              if (row.archived || row.requirement?.archived) return [];

              const requireId = row.requireId;
              const requirement = row.requirement;
              const levelStartDate = row.levelStartDate || "";
              const canEditThisRequirement =
                showEditEducationRequirementActions &&
                !!requirement &&
                canEditEducationRequirementType(requirement.requirementDefinition?.requireType);

              const actions: Action[] = [];

              if (canEditThisRequirement) {
                actions.push({
                  text: "Edit Requirement",
                  section: "Manage",
                  testId: "edit-education-requirement-action",
                  onClick: () => {
                    setSelectedRequirement({ requirement, levelStartDate });
                  }
                });
              }

              actions.push({
                text: "Delete Requirement",
                iconName: "delete",
                destructive: true,
                onClick: () =>
                  setRequirementToDelete({
                    description: row.description,
                    requireId,
                    levelCode: row.levelCode
                  })
              });

              return actions;
            },
            actionId,
            setMenu,
            getRowId: (row) => row.id
          })
        ]
      : [])
  ];

  const programHistoryTable = (
    <Table
      data={tableData}
      columns={columns}
      loading={loading}
      data-testid={testId}
      hasActiveFilters={showArchived || (showArchivedChip && tableData.length === 0)}
      renderControls={
        showControls
          ? (_table, { onSortClick } = {}) => (
              <TableControls
                onSortClick={onSortClick}
                actions={tableControlActions}
                filterChips={
                  showArchivedChip
                    ? [
                        {
                          label: "Show Archived",
                          onClick: () => setShowArchived?.(!showArchived),
                          selected: showArchived
                        }
                      ]
                    : undefined
                }
                filterChipsAriaLabel={showArchivedChip ? "PGM history filters" : undefined}
              />
            )
          : undefined
      }
      getRowCanExpand={(row) => row.original.detailsCount > 0}
      renderExpandedRow={({ row }) => {
        if (row.original.details.length > 0) {
          return (
            <TableDetailsPanel
              data={row.original.details}
              columns={detailsColumns}
              title={`Requirements & Tests (${row.original.completedDetailsCount}/${row.original.detailsCount})`}
            />
          );
        }
        return <div />;
      }}
      renderNoDataEmptyState={() => (
        <EmptyState
          title="No PGM History"
          caption="This person does not have any Professional Golf Management Program history"
          iconName="school"
        />
      )}
      renderFilteredEmptyState={() => (
        <EmptyState
          title="No PGM History"
          caption={
            showArchived
              ? "This person does not have any Professional Golf Management Program history"
              : "Turn on Show Archived to see archived levels"
          }
          iconName="school"
        />
      )}
    />
  );

  return (
    <>
      <Menu />
      {hasMetrics && <MetricGrid metrics={metrics} layout="expanded" />}
      {hasHeadingCopy ? <TableHeading heading={headingCopy} body={headingBody} /> : <div className="pt-12" />}
      {programHistoryTable}
      <DeleteProgramRequirementDialog
        open={!!requirementToDelete}
        requirement={requirementToDelete}
        pgaId={personId}
        onClose={() => setRequirementToDelete(null)}
      />
      <DeleteEducationProgramLevelDialog
        open={!!levelToDelete}
        level={levelToDelete}
        pgaId={personId}
        onClose={() => setLevelToDelete(null)}
      />
      {selectedRequirement ? (
        <EditEducationRequirementModal
          open
          onClose={() => setSelectedRequirement(null)}
          pgaId={personId}
          requirement={selectedRequirement.requirement}
          levelStartDate={selectedRequirement.levelStartDate}
        />
      ) : null}
      {showAddLevelModal ? (
        <AddEducationProgramLevelModal open onClose={() => setShowAddLevelModal(false)} pgaId={personId} />
      ) : null}
      {selectedLevel ? (
        <EditEducationProgramLevelModal
          open
          onClose={() => setSelectedLevel(null)}
          pgaId={personId}
          level={selectedLevel}
        />
      ) : null}
      <ArchiveEducationProgramLevelDialog
        open={!!levelToArchive}
        level={levelToArchive}
        pgaId={personId}
        onClose={() => setLevelToArchive(null)}
      />
    </>
  );
}
